import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  ArrowLeft,
  Upload,
  FileText,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Clock,
  Save,
  RefreshCw,
  Shield,
  CreditCard,
  TrendingUp,
  Calendar,
  User,
  XCircle,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API = API_BASE;

// iSoftpull payment pattern legend (https://desk.isoftpull.com/portal/en/kb/articles/payment-pattern-descriptions)
const PATTERN_LEGEND = {
  'UR': { label: 'Sin calificar', color: '#94a3b8', textColor: 'white' },
  '0': { label: 'Muy nueva (sin uso)', color: '#94a3b8', textColor: 'white' },
  'E': { label: 'Saldo cero / al día', color: '#10b981', textColor: 'white' },
  '1': { label: 'Paga al día', color: '#10b981', textColor: 'white' },
  'C': { label: 'Al día', color: '#10b981', textColor: 'white' },
  'B': { label: 'Cambio de condición', color: '#94a3b8', textColor: 'white' },
  '2': { label: '30-59 días tarde', color: '#fbbf24', textColor: 'black' },
  '3': { label: '60-89 días tarde', color: '#f97316', textColor: 'white' },
  '4': { label: '90-119 días tarde', color: '#ef4444', textColor: 'white' },
  '5': { label: '120+ días tarde', color: '#dc2626', textColor: 'white' },
  '6': { label: 'Cuenta en cobranza', color: '#7c2d12', textColor: 'white' },
  '7A': { label: 'Capítulo 13 BK', color: '#581c87', textColor: 'white' },
  '7B': { label: 'Wage Earner Plan', color: '#581c87', textColor: 'white' },
  '8': { label: 'Repossession', color: '#0a0a0a', textColor: 'white' },
  '8A': { label: 'Repo voluntaria', color: '#0a0a0a', textColor: 'white' },
  '8P': { label: 'Pagando repo', color: '#525252', textColor: 'white' },
  '9': { label: 'Charge-off', color: '#0a0a0a', textColor: 'white' },
  '9P': { label: 'Pagando CO/colección', color: '#525252', textColor: 'white' },
  'F': { label: 'Foreclosure', color: '#0a0a0a', textColor: 'white' },
  'FP': { label: 'Foreclosure iniciado', color: '#0a0a0a', textColor: 'white' },
  'M': { label: 'BK 7/11/12 Petitioned', color: '#581c87', textColor: 'white' },
};

const PaymentPatternBars = ({ codes = [], maxBars = 24 }) => {
  if (!codes || codes.length === 0) {
    return <span className="text-xs text-muted-foreground italic">Sin patrón disponible</span>;
  }
  const visible = codes.slice(0, maxBars);
  return (
    <div className="flex items-center gap-0.5" data-testid="payment-pattern-bars">
      {visible.map((code, idx) => {
        const info = PATTERN_LEGEND[code] || { label: code, color: '#cbd5e1', textColor: 'black' };
        return (
          <div
            key={idx}
            title={`Mes ${idx + 1} (${idx === 0 ? 'más reciente' : `${idx} mes(es) atrás`}): ${code} - ${info.label}`}
            className="inline-flex items-center justify-center font-bold text-[9px] rounded-sm"
            style={{
              backgroundColor: info.color,
              color: info.textColor,
              width: '14px',
              height: '20px',
              minWidth: '14px',
            }}
          >
            {code}
          </div>
        );
      })}
      {codes.length > maxBars && (
        <span className="text-[10px] text-muted-foreground ml-1">+{codes.length - maxBars}</span>
      )}
    </div>
  );
};

const PaymentPatternLegend = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="payment-pattern-legend">
    {Object.entries(PATTERN_LEGEND).map(([code, info]) => (
      <div key={code} className="flex items-center gap-2">
        <div
          className="inline-flex items-center justify-center font-bold rounded-sm"
          style={{ backgroundColor: info.color, color: info.textColor, width: '20px', height: '20px' }}
        >
          {code}
        </div>
        <span className="text-foreground/80">{info.label}</span>
      </div>
    ))}
  </div>
);

const CreditReportAnalysisApp = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [analystNotes, setAnalystNotes] = useState('');
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryText, setSummaryText] = useState('');

  useEffect(() => {
    if (id) fetchAnalysis();
  }, [id]);

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/credit-report-analyses/${id}`);
      setAnalysis(response.data);
      setAnalystNotes(response.data.analyst_notes || '');
      setSummaryText(response.data.executive_summary || '');
    } catch (error) {
      toast.error('Error al cargar análisis');
      navigate('/apps/credit-report');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    const file = files[0];
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Solo se aceptan archivos PDF');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/credit-report-analyses/${id}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setAnalysis(response.data);
      setSummaryText(response.data.executive_summary || '');
      
      if (response.data.status === 'clean_credit_review') {
        toast.success('✅ Análisis completado - Sin eventos de alto riesgo');
      } else {
        toast.warning(`⚠️ ${response.data.high_risk_count} evento(s) de alto riesgo detectados`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el reporte');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.put(`${API}/credit-report-analyses/${id}/notes?notes=${encodeURIComponent(analystNotes)}`);
      toast.success('Notas guardadas');
    } catch (error) {
      toast.error('Error al guardar notas');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSaveSummary = async () => {
    try {
      await axios.put(`${API}/credit-report-analyses/${id}/summary?executive_summary=${encodeURIComponent(summaryText)}`);
      setAnalysis(prev => ({ ...prev, executive_summary: summaryText }));
      setEditingSummary(false);
      toast.success('Resumen actualizado');
    } catch (error) {
      toast.error('Error al actualizar resumen');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'clean_credit_review':
        return (
          <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 text-lg py-2 px-4">
            <CheckCircle className="w-5 h-5 mr-2" />
            Clean Credit Review ✅
          </Badge>
        );
      case 'review_required':
        return (
          <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 text-lg py-2 px-4">
            <AlertTriangle className="w-5 h-5 mr-2" />
            Review Required ⚠️
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="text-lg py-2 px-4">
            <Clock className="w-5 h-5 mr-2" />
            Pendiente de Análisis
          </Badge>
        );
    }
  };

  const getSeverityBadge = (severity) => {
    if (severity === 'CRITICAL') {
      return <Badge className="bg-red-600 text-white">CRÍTICO</Badge>;
    }
    if (severity === 'HIGH') {
      return <Badge className="bg-orange-500 text-white">ALTO</Badge>;
    }
    if (severity === 'MEDIUM') {
      return <Badge className="bg-amber-500 text-white">MEDIO</Badge>;
    }
    return <Badge className="bg-blue-500 text-white">BAJO</Badge>;
  };

  if (loading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate('/apps/credit-report')} data-testid="back-to-list-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2 flex-wrap">
            Análisis de Reporte de Crédito
            {analysis?.bureau && (
              <Badge
                className={analysis?.is_legacy ? "bg-amber-200 text-amber-900 border border-amber-400" : "bg-red-600 text-white"}
                data-testid="header-bureau-badge"
              >
                {analysis?.is_legacy ? `Legacy: ${analysis.bureau}` : analysis.bureau}
              </Badge>
            )}
            {analysis?.decision_tag?.tag && (
              <Badge
                style={{ backgroundColor: analysis.decision_tag.color_hex, color: '#fff' }}
                className="text-base px-3 py-1"
                data-testid="header-decision-tag"
              >
                {analysis.decision_tag.label || analysis.decision_tag.tag}
              </Badge>
            )}
          </h1>
          <p className="text-muted-foreground">
            {analysis?.applicant?.name && <span>Solicitante: <span className="font-medium text-foreground">{analysis.applicant.name}</span> • </span>}
            Creado: {new Date(analysis?.created_at).toLocaleDateString()}
          </p>
        </div>
        <Button
          variant="default"
          size="sm"
          onClick={async () => {
            try {
              const response = await axios.post(`${API}/credit-report-analyses`, {
                source_provider: 'equifax',
                client_id: null,
                source_notes: null
              });
              toast.success('Nuevo análisis creado');
              navigate(`/apps/credit-report/${response.data.id}`);
            } catch (error) {
              toast.error('Error al crear nuevo análisis');
            }
          }}
          data-testid="upload-another-btn"
          className="bg-red-600 hover:bg-red-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Subir otro reporte
        </Button>
        {getStatusBadge(analysis?.status)}
      </div>

      {/* Legacy Report Banner */}
      {analysis?.is_legacy && (
        <Card className="border-amber-400 bg-amber-50 dark:bg-amber-900/10" data-testid="legacy-banner">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-amber-800 dark:text-amber-300">Legacy Report ({analysis?.bureau || 'Unknown'})</h3>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Este reporte fue generado con un formato anterior. El sistema ahora está estandarizado en <strong>iSoftpull Equifax</strong>. La información se conserva por trazabilidad.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Decision Tag Card */}
      {analysis?.decision_tag?.tag && analysis?.status !== 'pending' && (
        <Card
          className="border-2"
          style={{ borderColor: analysis.decision_tag.color_hex }}
          data-testid="decision-tag-card"
        >
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-3">
              <Shield className="w-6 h-6" style={{ color: analysis.decision_tag.color_hex }} />
              <span>Decisión de Underwriting</span>
              <Badge
                style={{ backgroundColor: analysis.decision_tag.color_hex, color: '#fff' }}
                className="text-base px-3 py-1"
              >
                {analysis.decision_tag.label || analysis.decision_tag.tag}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-foreground/80 mb-2" data-testid="decision-reasoning">
              {analysis.decision_tag.reasoning}
            </p>
            {analysis.decision_tag.down_payment_range && (
              <p className="text-sm">
                <strong>Rango de enganche sugerido:</strong> <Badge variant="outline" className="ml-1" data-testid="down-payment-range">{analysis.decision_tag.down_payment_range}</Badge>
              </p>
            )}
            {analysis.decision_tag.active_events?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {analysis.decision_tag.active_events.map((ev, idx) => (
                  <Badge key={idx} variant="destructive" className="text-xs">
                    {ev.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Applicant Info Card */}
      {analysis?.applicant && Object.keys(analysis.applicant).length > 0 && (
        <Card data-testid="applicant-info-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="w-4 h-4" />
              Información del Solicitante
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              {analysis.applicant.name && (
                <div><p className="text-muted-foreground">Nombre</p><p className="font-medium">{analysis.applicant.name}</p></div>
              )}
              {analysis.applicant.dob && (
                <div><p className="text-muted-foreground">Fecha de Nacimiento</p><p className="font-medium">{analysis.applicant.dob}</p></div>
              )}
              {analysis.applicant.ssn_masked && (
                <div><p className="text-muted-foreground">SSN</p><p className="font-medium">{analysis.applicant.ssn_masked}</p></div>
              )}
              {analysis.applicant.current_employer && (
                <div><p className="text-muted-foreground">Empleo Actual</p><p className="font-medium">{analysis.applicant.current_employer}</p></div>
              )}
              {analysis.applicant.current_street && (
                <div className="col-span-2 md:col-span-2">
                  <p className="text-muted-foreground">Dirección Actual</p>
                  <p className="font-medium">{analysis.applicant.current_street}{analysis.applicant.current_city ? `, ${analysis.applicant.current_city}` : ''}{analysis.applicant.current_state ? `, ${analysis.applicant.current_state}` : ''} {analysis.applicant.current_zip || ''}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Section */}
      {analysis?.status === 'pending' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Cargar Reporte de Crédito (PDF)
            </CardTitle>
            <CardDescription>
              Suba el reporte de crédito Soft Pull del proveedor autorizado
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="font-medium mb-2">Arrastra el PDF aquí o haz clic para seleccionar</p>
              <p className="text-sm text-muted-foreground mb-4">Solo archivos PDF</p>
              
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => handleUpload(Array.from(e.target.files))}
                className="hidden"
                id="credit-report-upload"
              />
              
              <Button 
                onClick={() => document.getElementById('credit-report-upload').click()}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Analizando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Seleccionar PDF
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analysis Results */}
      {analysis?.status !== 'pending' && (
        <>
          {/* Status Alert */}
          <Card className={cn(
            "border-2",
            analysis?.status === 'clean_credit_review' 
              ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/10" 
              : "border-red-500 bg-red-50 dark:bg-red-900/10"
          )}>
            <CardContent className="py-6">
              <div className="flex items-start gap-4">
                {analysis?.status === 'clean_credit_review' ? (
                  <CheckCircle className="w-10 h-10 text-emerald-600" />
                ) : (
                  <AlertTriangle className="w-10 h-10 text-red-600" />
                )}
                <div>
                  {analysis?.status === 'clean_credit_review' ? (
                    <h3 className="text-xl font-bold text-emerald-700 dark:text-emerald-400">
                      ✅ FELICIDADES, NO HEMOS ENCONTRADO EVENTOS DE ALTO RIESGO EN ESTE REPORTE DE CRÉDITO.
                    </h3>
                  ) : (
                    <>
                      <h3 className="text-xl font-bold text-red-700 dark:text-red-400">
                        ⚠️ Se han detectado eventos de alto riesgo en este reporte de crédito.
                      </h3>
                      <p className="text-red-600 dark:text-red-400 mt-1">
                        Se requiere análisis manual del analista.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* High Risk Events Table - enriched with iSoftpull legend codes */}
          {analysis?.high_risk_events?.length > 0 && (() => {
            const events = analysis.high_risk_events;
            const activeEvents = events.filter(e => e.is_active);
            const resolvedEvents = events.filter(e => !e.is_active);
            return (
              <Card className="border-red-200 dark:border-red-900" data-testid="high-risk-events-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                    <AlertCircle className="w-5 h-5" />
                    Eventos de Alto Riesgo Detectados ({analysis.high_risk_count})
                  </CardTitle>
                  <CardDescription>
                    Mapeo automático de la leyenda iSoftpull + inferencias del lending de equipo (ej: charge-off auto = repossession)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {activeEvents.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-red-600 animate-pulse"></span>
                        Eventos ACTIVOS ({activeEvents.length})
                      </h4>
                      <Table data-testid="active-events-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Código</TableHead>
                            <TableHead>Cuenta / Tradeline</TableHead>
                            <TableHead>Fecha</TableHead>
                            <TableHead>Monto</TableHead>
                            <TableHead>Severidad</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {activeEvents.map((event, idx) => (
                            <TableRow key={`active-${idx}`} className="bg-red-50/70 dark:bg-red-900/20 border-l-4 border-l-red-600">
                              <TableCell className="font-medium">
                                {event.event_type?.replace(/_/g, ' ')}
                                {event.inferred_from === 'AUTO_CHARGEOFF' && (
                                  <Badge className="ml-2 bg-orange-200 text-orange-900 text-[10px]">INFERIDA</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {event.pattern_code ? (
                                  <div className="flex items-center gap-1">
                                    <div
                                      className="inline-flex items-center justify-center font-bold text-[10px] rounded-sm"
                                      style={{
                                        backgroundColor: PATTERN_LEGEND[event.pattern_code]?.color || '#cbd5e1',
                                        color: PATTERN_LEGEND[event.pattern_code]?.textColor || 'black',
                                        width: '22px',
                                        height: '22px'
                                      }}
                                    >
                                      {event.pattern_code}
                                    </div>
                                    {event.months_ago !== undefined && event.months_ago !== null && (
                                      <span className="text-[10px] text-muted-foreground">
                                        {event.months_ago === 0 ? 'mes actual' : `hace ${event.months_ago}m`}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">{event.category || '-'}</span>
                                )}
                              </TableCell>
                              <TableCell className="max-w-[260px] truncate" title={event.tradeline || event.account_name}>
                                {event.tradeline || event.account_name || '-'}
                              </TableCell>
                              <TableCell>{event.date_reported || '-'}</TableCell>
                              <TableCell>
                                {event.amount ? `$${Number(event.amount).toLocaleString()}` : '-'}
                              </TableCell>
                              <TableCell>{getSeverityBadge(event.severity)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  {resolvedEvents.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-amber-500"></span>
                        Eventos HISTÓRICOS / RESUELTOS ({resolvedEvents.length})
                      </h4>
                      <Table data-testid="resolved-events-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Código</TableHead>
                            <TableHead>Cuenta / Tradeline</TableHead>
                            <TableHead>Fecha</TableHead>
                            <TableHead>Monto</TableHead>
                            <TableHead>Severidad</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {resolvedEvents.slice(0, 30).map((event, idx) => (
                            <TableRow key={`resolved-${idx}`} className="bg-amber-50/40 dark:bg-amber-900/10">
                              <TableCell className="font-medium">
                                {event.event_type?.replace(/_/g, ' ')}
                                {event.inferred_from === 'AUTO_CHARGEOFF' && (
                                  <Badge className="ml-2 bg-orange-200 text-orange-900 text-[10px]">INFERIDA</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {event.pattern_code ? (
                                  <div className="flex items-center gap-1">
                                    <div
                                      className="inline-flex items-center justify-center font-bold text-[10px] rounded-sm"
                                      style={{
                                        backgroundColor: PATTERN_LEGEND[event.pattern_code]?.color || '#cbd5e1',
                                        color: PATTERN_LEGEND[event.pattern_code]?.textColor || 'black',
                                        width: '22px',
                                        height: '22px'
                                      }}
                                    >
                                      {event.pattern_code}
                                    </div>
                                    {event.months_ago !== undefined && event.months_ago !== null && (
                                      <span className="text-[10px] text-muted-foreground">
                                        hace {event.months_ago}m
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">{event.category || '-'}</span>
                                )}
                              </TableCell>
                              <TableCell className="max-w-[260px] truncate" title={event.tradeline || event.account_name}>
                                {event.tradeline || event.account_name || '-'}
                              </TableCell>
                              <TableCell>{event.date_reported || '-'}</TableCell>
                              <TableCell>
                                {event.amount ? `$${Number(event.amount).toLocaleString()}` : '-'}
                              </TableCell>
                              <TableCell>{getSeverityBadge(event.severity)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {resolvedEvents.length > 30 && (
                        <p className="text-xs text-muted-foreground mt-2">Mostrando 30 de {resolvedEvents.length} eventos resueltos.</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Summary Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {/* FICO Score */}
            <Card className={cn(
              "border-2",
              analysis?.fico_score >= 700 ? "border-emerald-300" :
              analysis?.fico_score >= 650 ? "border-amber-300" : "border-red-300"
            )} data-testid="metric-fico">
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-1">FICO Score (Referencia)</p>
                  <p className={cn(
                    "text-4xl font-bold",
                    analysis?.fico_score >= 700 ? "text-emerald-600" :
                    analysis?.fico_score >= 650 ? "text-amber-600" : "text-red-600"
                  )}>
                    {analysis?.fico_score || 'N/A'}
                  </p>
                  <p className="text-[10px] text-muted-foreground italic mt-1">No es factor de decisión</p>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="metric-tradelines">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Tradelines</p>
                    <p className="text-2xl font-bold">{analysis?.total_tradelines || 0}</p>
                    <p className="text-xs text-muted-foreground">
                      {analysis?.total_active_accounts || 0} abiertas
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="metric-public-records">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-2 rounded-lg",
                    analysis?.public_records_count > 0 ? "bg-red-100 dark:bg-red-900/30" : "bg-emerald-100 dark:bg-emerald-900/30"
                  )}>
                    <Shield className={cn(
                      "w-5 h-5",
                      analysis?.public_records_count > 0 ? "text-red-600" : "text-emerald-600"
                    )} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Public Records</p>
                    <p className={cn(
                      "text-2xl font-bold",
                      analysis?.public_records_count > 0 ? "text-red-600" : "text-emerald-600"
                    )}>
                      {analysis?.public_records_count || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="metric-collections">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-2 rounded-lg",
                    analysis?.collections_count > 0 ? "bg-red-100 dark:bg-red-900/30" : "bg-emerald-100 dark:bg-emerald-900/30"
                  )}>
                    <AlertCircle className={cn(
                      "w-5 h-5",
                      analysis?.collections_count > 0 ? "text-red-600" : "text-emerald-600"
                    )} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Collections</p>
                    <p className={cn(
                      "text-2xl font-bold",
                      analysis?.collections_count > 0 ? "text-red-600" : "text-emerald-600"
                    )}>
                      {analysis?.collections_count || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="metric-utilization">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Utilización</p>
                    <p className={cn(
                      "text-2xl font-bold",
                      (analysis?.overall_utilization || 0) > 50 ? "text-red-600" : "text-emerald-600"
                    )}>
                      {analysis?.overall_utilization !== null && analysis?.overall_utilization !== undefined
                        ? `${analysis.overall_utilization}%`
                        : 'N/A'}
                    </p>
                    <p className="text-xs text-muted-foreground">Cuentas abiertas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Open Accounts Aggregate (from Credit Report Summary) */}
          {(analysis?.total_credit_limit || analysis?.total_open_balance) && (
            <Card data-testid="open-accounts-aggregate">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <CreditCard className="w-4 h-4" />
                  Resumen de Cuentas Abiertas (Credit Report Summary)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 border" data-testid="agg-credit-limit">
                    <p className="text-xs text-muted-foreground">Límite Total Revolvente</p>
                    <p className="text-xl font-bold">
                      {analysis?.total_credit_limit !== null && analysis?.total_credit_limit !== undefined
                        ? `$${Number(analysis.total_credit_limit).toLocaleString()}`
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200" data-testid="agg-available-credit">
                    <p className="text-xs text-muted-foreground">Crédito Disponible</p>
                    <p className="text-xl font-bold text-emerald-700">
                      {analysis?.total_available_credit !== null && analysis?.total_available_credit !== undefined
                        ? `$${Number(analysis.total_available_credit).toLocaleString()}`
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200" data-testid="agg-balance">
                    <p className="text-xs text-muted-foreground">Balance Total</p>
                    <p className="text-xl font-bold text-amber-700">
                      {analysis?.total_open_balance !== null && analysis?.total_open_balance !== undefined
                        ? `$${Number(analysis.total_open_balance).toLocaleString()}`
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200" data-testid="agg-monthly">
                    <p className="text-xs text-muted-foreground">Pagos Mensuales</p>
                    <p className="text-xl font-bold text-blue-700">
                      {analysis?.total_monthly_payments !== null && analysis?.total_monthly_payments !== undefined
                        ? `$${Number(analysis.total_monthly_payments).toLocaleString()}`
                        : 'N/A'}
                    </p>
                  </div>
                  <div className={cn(
                    "p-3 rounded-lg border",
                    (analysis?.overall_utilization || 0) > 70 ? "bg-red-50 border-red-200" :
                    (analysis?.overall_utilization || 0) > 30 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"
                  )} data-testid="agg-utilization">
                    <p className="text-xs text-muted-foreground">Ratio de Utilización</p>
                    <p className={cn(
                      "text-xl font-bold",
                      (analysis?.overall_utilization || 0) > 70 ? "text-red-700" :
                      (analysis?.overall_utilization || 0) > 30 ? "text-amber-700" : "text-emerald-700"
                    )}>
                      {analysis?.overall_utilization !== null && analysis?.overall_utilization !== undefined
                        ? `${analysis.overall_utilization}%`
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Late Payments & Inquiries */}
          <Card data-testid="late-payments-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Historial de Pagos Tardíos (30/60/90 Días)
              </CardTitle>
              <CardDescription>
                Reportado en el Credit Report Summary (autoritativo) + desglose de cuentas activas en últimos 3 años
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Authoritative summary counts */}
              <div className="mb-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2 font-semibold">Total reportado por el buró</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className={cn(
                    "p-4 rounded-lg border-2",
                    (analysis?.late_payments?.['30_days'] || 0) > 0 ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"
                  )} data-testid="summary-late-30">
                    <p className="text-sm text-muted-foreground">30 Días</p>
                    <p className={cn(
                      "text-3xl font-bold",
                      (analysis?.late_payments?.['30_days'] || 0) > 0 ? "text-amber-600" : "text-emerald-600"
                    )}>
                      {analysis?.late_payments?.['30_days'] || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">incidentes</p>
                  </div>
                  <div className={cn(
                    "p-4 rounded-lg border-2",
                    (analysis?.late_payments?.['60_days'] || 0) > 0 ? "border-orange-300 bg-orange-50" : "border-emerald-300 bg-emerald-50"
                  )} data-testid="summary-late-60">
                    <p className="text-sm text-muted-foreground">60 Días</p>
                    <p className={cn(
                      "text-3xl font-bold",
                      (analysis?.late_payments?.['60_days'] || 0) > 0 ? "text-orange-600" : "text-emerald-600"
                    )}>
                      {analysis?.late_payments?.['60_days'] || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">incidentes</p>
                  </div>
                  <div className={cn(
                    "p-4 rounded-lg border-2",
                    (analysis?.late_payments?.['90_days'] || 0) > 0 ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50"
                  )} data-testid="summary-late-90">
                    <p className="text-sm text-muted-foreground">90+ Días</p>
                    <p className={cn(
                      "text-3xl font-bold",
                      (analysis?.late_payments?.['90_days'] || 0) > 0 ? "text-red-600" : "text-emerald-600"
                    )}>
                      {analysis?.late_payments?.['90_days'] || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">incidentes</p>
                  </div>
                </div>
              </div>

              {/* Recent 3-year breakdown by account */}
              {analysis?.late_payments_recent_3y && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2 font-semibold">
                    Cuentas con lates en últimos 3 años (detalle)
                  </p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div data-testid="recent-3y-30">
                      <span className="font-medium">30d:</span> {analysis.late_payments_recent_3y.accounts_30_days || 0} cuenta(s) — {analysis.late_payments_recent_3y['30_days'] || 0} incidente(s)
                    </div>
                    <div data-testid="recent-3y-60">
                      <span className="font-medium">60d:</span> {analysis.late_payments_recent_3y.accounts_60_days || 0} cuenta(s) — {analysis.late_payments_recent_3y['60_days'] || 0} incidente(s)
                    </div>
                    <div data-testid="recent-3y-90">
                      <span className="font-medium">90d:</span> {analysis.late_payments_recent_3y.accounts_90_days || 0} cuenta(s) — {analysis.late_payments_recent_3y['90_days'] || 0} incidente(s)
                    </div>
                  </div>
                </div>
              )}
              
              <p className="text-xs text-muted-foreground mt-4 italic">
                * Los totales de la parte superior provienen directamente del Credit Report Summary del buró. El desglose por cuenta abajo se calcula sobre cuentas abiertas en últimos 3 años.
              </p>
              {/* Detailed breakdown of accounts with late payments in last 3 years */}
              {analysis?.late_payments_detail?.accounts_30_days?.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium mb-2 text-amber-700">Cuentas con 30 días late:</h4>
                  <div className="space-y-1">
                    {analysis.late_payments_detail.accounts_30_days.map((acc, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-amber-50 rounded text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{acc.account_type}</span>
                          {acc.credit_limit && <span className="text-xs text-muted-foreground">(Límite: ${Number(acc.credit_limit).toLocaleString()})</span>}
                          {acc.status === 'Closed' && <Badge variant="outline" className="text-xs">Cerrada</Badge>}
                        </div>
                        <span className="text-muted-foreground">
                          Abierta: {acc.date_opened || 'N/A'} - {acc.late_count}x late
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {analysis?.late_payments_detail?.accounts_60_days?.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium mb-2 text-orange-700">Cuentas con 60 días late:</h4>
                  <div className="space-y-1">
                    {analysis.late_payments_detail.accounts_60_days.map((acc, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-orange-50 rounded text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{acc.account_type}</span>
                          {acc.credit_limit && <span className="text-xs text-muted-foreground">(Límite: ${Number(acc.credit_limit).toLocaleString()})</span>}
                          {acc.status === 'Closed' && <Badge variant="outline" className="text-xs">Cerrada</Badge>}
                        </div>
                        <span className="text-muted-foreground">
                          Abierta: {acc.date_opened || 'N/A'} - {acc.late_count}x late
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {analysis?.late_payments_detail?.accounts_90_days?.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium mb-2 text-red-700">Cuentas con 90+ días late:</h4>
                  <div className="space-y-1">
                    {analysis.late_payments_detail.accounts_90_days.map((acc, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-red-50 rounded text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{acc.account_type}</span>
                          {acc.credit_limit && <span className="text-xs text-muted-foreground">(Límite: ${Number(acc.credit_limit).toLocaleString()})</span>}
                          {acc.status === 'Closed' && <Badge variant="outline" className="text-xs">Cerrada</Badge>}
                        </div>
                        <span className="text-muted-foreground">
                          Abierta: {acc.date_opened || 'N/A'} - {acc.late_count}x late
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Inquiries */}
              <div className="mt-6 pt-4 border-t">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "p-2 rounded-lg",
                    (analysis?.inquiries_count || 0) > 10 ? "bg-amber-100" : "bg-blue-100"
                  )}>
                    <Calendar className={cn(
                      "w-5 h-5",
                      (analysis?.inquiries_count || 0) > 10 ? "text-amber-600" : "text-blue-600"
                    )} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Inquiries (Consultas)</p>
                    <p className="text-2xl font-bold">{analysis?.inquiries_count || 0}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Resumen de Cuentas por Tipo */}
          {analysis?.accounts_by_type?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Resumen de Cuentas Activas por Tipo
                </CardTitle>
                <CardDescription>
                  Análisis agrupado por tipo de cuenta con Balance/Límite
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo de Cuenta</TableHead>
                      <TableHead className="text-center"># Cuentas</TableHead>
                      <TableHead className="text-right">Balance Total</TableHead>
                      <TableHead className="text-right">Límite Total</TableHead>
                      <TableHead className="text-center">Balance/Límite (%)</TableHead>
                      <TableHead className="text-right">Pago Mensual</TableHead>
                      <TableHead className="text-center">Meses Activos (Prom)</TableHead>
                      <TableHead className="text-center">Meses a Tiempo (Prom)</TableHead>
                      <TableHead className="text-center">Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analysis.accounts_by_type.map((typeGroup, idx) => {
                      const utilizationPct = typeGroup.total_limit > 0 
                        ? ((typeGroup.total_balance / typeGroup.total_limit) * 100).toFixed(1)
                        : null;
                      
                      return (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">
                            {typeGroup.account_type}
                          </TableCell>
                          <TableCell className="text-center font-medium">
                            {typeGroup.count}
                          </TableCell>
                          <TableCell className="text-right">
                            ${typeGroup.total_balance?.toLocaleString() || '0'}
                          </TableCell>
                          <TableCell className="text-right">
                            {typeGroup.total_limit > 0 
                              ? `$${typeGroup.total_limit.toLocaleString()}`
                              : '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            {utilizationPct !== null ? (
                              <Badge className={cn(
                                parseFloat(utilizationPct) > 80 && "bg-red-100 text-red-800",
                                parseFloat(utilizationPct) <= 80 && parseFloat(utilizationPct) > 50 && "bg-amber-100 text-amber-800",
                                parseFloat(utilizationPct) <= 50 && "bg-emerald-100 text-emerald-800"
                              )}>
                                {utilizationPct}%
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">N/A</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {(typeGroup.total_monthly_payment || 0) === 0 ? (
                              <span className="text-amber-600 flex items-center justify-end gap-1" title="Verificar manualmente">
                                $0 <AlertTriangle className="w-3 h-3" />
                              </span>
                            ) : (
                              <span>${(typeGroup.total_monthly_payment || 0).toLocaleString()}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center font-medium">
                            {typeGroup.avg_months_active || 0}
                          </TableCell>
                          <TableCell className="text-center text-emerald-600 font-medium">
                            {typeGroup.avg_months_on_time || 0}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center gap-1">
                              {typeGroup.good_standing_count > 0 && (
                                <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                                  {typeGroup.good_standing_count} OK
                                </Badge>
                              )}
                              {typeGroup.late_history_count > 0 && (
                                <Badge className="bg-amber-100 text-amber-800 text-xs">
                                  {typeGroup.late_history_count} Late
                                </Badge>
                              )}
                              {typeGroup.past_due_count > 0 && (
                                <Badge className="bg-red-100 text-red-800 text-xs">
                                  {typeGroup.past_due_count} Vencida
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Fila Total */}
                    <TableRow className="bg-muted/50 font-bold border-t-2">
                      <TableCell className="font-bold">TOTAL</TableCell>
                      <TableCell className="text-center font-bold">
                        {analysis.accounts_by_type.reduce((sum, t) => sum + t.count, 0)}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        ${analysis.accounts_by_type.reduce((sum, t) => sum + (t.total_balance || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        ${analysis.accounts_by_type.reduce((sum, t) => sum + (t.total_limit || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center font-bold">
                        {(() => {
                          const totalBalance = analysis.accounts_by_type.reduce((sum, t) => sum + (t.total_balance || 0), 0);
                          const totalLimit = analysis.accounts_by_type.reduce((sum, t) => sum + (t.total_limit || 0), 0);
                          const pct = totalLimit > 0 ? ((totalBalance / totalLimit) * 100).toFixed(1) : null;
                          return pct ? (
                            <Badge className={cn(
                              parseFloat(pct) > 80 && "bg-red-100 text-red-800",
                              parseFloat(pct) <= 80 && parseFloat(pct) > 50 && "bg-amber-100 text-amber-800",
                              parseFloat(pct) <= 50 && "bg-emerald-100 text-emerald-800"
                            )}>
                              {pct}%
                            </Badge>
                          ) : '-';
                        })()}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        ${analysis.accounts_by_type.reduce((sum, t) => sum + (t.total_monthly_payment || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center font-bold">
                        {(analysis.accounts_by_type.reduce((sum, t) => sum + (t.avg_months_active || 0) * t.count, 0) / 
                          analysis.accounts_by_type.reduce((sum, t) => sum + t.count, 0)).toFixed(1)}
                      </TableCell>
                      <TableCell className="text-center font-bold text-emerald-600">
                        {(analysis.accounts_by_type.reduce((sum, t) => sum + (t.avg_months_on_time || 0) * t.count, 0) / 
                          analysis.accounts_by_type.reduce((sum, t) => sum + t.count, 0)).toFixed(1)}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                {analysis.accounts_by_type.some(t => (t.total_monthly_payment || 0) === 0) && (
                  <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Los valores de $0 en Pago Mensual requieren verificación manual - el reporte puede no incluir esta información.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Detalle Individual de Cuentas Activas */}
          {analysis?.active_accounts?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Detalle Individual de Cuentas ({analysis.active_accounts.length})
                </CardTitle>
                <CardDescription>
                  Lista completa de cuentas activas con Balance/Límite
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Acreedor</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Apertura</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead className="text-right">Límite</TableHead>
                      <TableHead className="text-center">Bal/Lím (%)</TableHead>
                      <TableHead className="text-right">Pago</TableHead>
                      <TableHead className="text-center">Meses Activo</TableHead>
                      <TableHead className="text-center">Late 30/60/90</TableHead>
                      <TableHead className="text-center min-w-[280px]">Payment Pattern (más reciente →)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analysis.active_accounts.slice(0, 30).map((account, idx) => {
                      const rating = (account.account_rating || '').toUpperCase();
                      const remarks = (account.remarks || '').toUpperCase();
                      const hasCritical = /REPOSSESS|CHARGE-OFF|CHARGED OFF|JUDGMENT|LIEN|BANKRUPT|FORECLOSURE|CHILD SUPPORT|GARNISH/.test(rating + ' ' + remarks);
                      const hasLate = (account.late_payments_30 || 0) + (account.late_payments_60 || 0) + (account.late_payments_90 || 0) > 0;
                      const isCurrent = !hasCritical && !hasLate && account.is_good_standing;
                      const rowClass = hasCritical
                        ? 'bg-red-50 dark:bg-red-950/30 border-l-4 border-l-red-500'
                        : hasLate
                          ? 'bg-amber-50 dark:bg-amber-950/30 border-l-4 border-l-amber-500'
                          : isCurrent
                            ? 'border-l-4 border-l-emerald-500'
                            : '';
                      return (
                      <TableRow key={idx} className={rowClass} data-testid={`tradeline-row-${idx}`}>
                        <TableCell className="font-medium max-w-[150px] truncate" title={account.creditor_name}>
                          {account.creditor_name || '-'}
                        </TableCell>
                        <TableCell className="max-w-[120px] truncate" title={account.account_type}>
                          {account.account_type}
                        </TableCell>
                        <TableCell>
                          <Badge className={cn(
                            (account.current_status === 'Current' || account.current_status === 'Open') && !hasCritical && "bg-emerald-100 text-emerald-800",
                            account.current_status === 'Past Due' && "bg-red-100 text-red-800",
                            account.current_status === 'Closed' && "bg-gray-100 text-gray-800",
                            (account.current_status === 'Charge-Off' || hasCritical) && "bg-red-600 text-white"
                          )}>
                            {hasCritical && rating ? (rating.length > 16 ? rating.substring(0,16) + '…' : rating) : (account.current_status === 'Open' ? 'Activa' : account.current_status)}
                          </Badge>
                        </TableCell>
                        <TableCell>{account.date_opened || '-'}</TableCell>
                        <TableCell className="text-right">
                          {account.current_balance !== null && account.current_balance !== undefined
                            ? `$${account.current_balance.toLocaleString()}`
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {account.credit_limit 
                            ? `$${account.credit_limit.toLocaleString()}` 
                            : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {account.utilization_pct !== null && account.utilization_pct !== undefined ? (
                            <Badge className={cn(
                              account.utilization_pct > 80 && "bg-red-100 text-red-800",
                              account.utilization_pct <= 80 && account.utilization_pct > 50 && "bg-amber-100 text-amber-800",
                              account.utilization_pct <= 50 && "bg-emerald-100 text-emerald-800"
                            )}>
                              {account.utilization_pct.toFixed(0)}%
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {(account.monthly_payment || 0) === 0 ? (
                            <span className="text-muted-foreground">-</span>
                          ) : (
                            <span>${(account.monthly_payment || 0).toLocaleString()}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {account.months_active || 0}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={cn(
                            (account.late_payments_30 > 0 || account.late_payments_60 > 0 || account.late_payments_90 > 0) && "text-red-600 font-medium"
                          )}>
                            {account.late_payments_30 || 0}/{account.late_payments_60 || 0}/{account.late_payments_90 || 0}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <PaymentPatternBars codes={account.payment_pattern_codes || []} maxBars={24} />
                          {account.pattern_summary?.recent_critical_event && (
                            <Badge className="mt-1 bg-red-600 text-white text-[9px] px-1 py-0" data-testid={`pattern-critical-${idx}`}>
                              CRÍTICO RECIENTE
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                    {/* Fila Total */}
                    <TableRow className="bg-muted/50 font-bold border-t-2">
                      <TableCell className="font-bold">TOTAL</TableCell>
                      <TableCell></TableCell>
                      <TableCell className="font-bold text-center">
                        {analysis.active_accounts.length}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right font-bold">
                        ${analysis.active_accounts.reduce((sum, a) => sum + (a.current_balance || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        ${analysis.active_accounts.reduce((sum, a) => sum + (a.credit_limit || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center font-bold">
                        {(() => {
                          const totalBalance = analysis.active_accounts.reduce((sum, a) => sum + (a.current_balance || 0), 0);
                          const totalLimit = analysis.active_accounts.reduce((sum, a) => sum + (a.credit_limit || 0), 0);
                          const pct = totalLimit > 0 ? ((totalBalance / totalLimit) * 100).toFixed(1) : null;
                          return pct ? (
                            <Badge className={cn(
                              parseFloat(pct) > 80 && "bg-red-100 text-red-800",
                              parseFloat(pct) <= 80 && parseFloat(pct) > 50 && "bg-amber-100 text-amber-800",
                              parseFloat(pct) <= 50 && "bg-emerald-100 text-emerald-800"
                            )}>
                              {pct}%
                            </Badge>
                          ) : '-';
                        })()}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        ${analysis.active_accounts.reduce((sum, a) => sum + (a.monthly_payment || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center font-bold">
                        {(analysis.active_accounts.reduce((sum, a) => sum + (a.months_active || 0), 0) / analysis.active_accounts.length).toFixed(1)}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                {analysis.active_accounts.some(a => (a.monthly_payment || 0) === 0) && (
                  <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Los valores de $0 en Pago Mensual requieren verificación manual - el reporte puede no incluir esta información.
                  </p>
                )}
                {analysis.active_accounts.length > 30 && (
                  <p className="text-sm text-muted-foreground mt-4 text-center">
                    Mostrando 30 de {analysis.active_accounts.length} cuentas
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Payment Pattern Legend */}
          {analysis?.active_accounts?.some(a => (a.payment_pattern_codes || []).length > 0) && (
            <Card data-testid="pattern-legend-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calendar className="w-4 h-4" />
                  Leyenda Payment Pattern (iSoftpull)
                </CardTitle>
                <CardDescription>
                  Cada cuadrito representa un mes. El cuadrito más a la izquierda es el mes más reciente. Esta leyenda es la fuente de verdad usada por el algoritmo de riesgo.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PaymentPatternLegend />
                {analysis?.risk_recommendation?.pattern_aggregate && (
                  <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    <div data-testid="pattern-agg-total">
                      <p className="text-xs text-muted-foreground">Códigos totales</p>
                      <p className="font-bold">{analysis.risk_recommendation.pattern_aggregate.total_codes || 0}</p>
                    </div>
                    <div data-testid="pattern-agg-ok">
                      <p className="text-xs text-muted-foreground">Códigos OK (1/E/C)</p>
                      <p className="font-bold text-emerald-600">{analysis.risk_recommendation.pattern_aggregate.ok_codes || 0}</p>
                    </div>
                    <div data-testid="pattern-agg-critical">
                      <p className="text-xs text-muted-foreground">Cuentas con código crítico reciente (24m)</p>
                      <p className={cn("font-bold", (analysis.risk_recommendation.pattern_aggregate.recent_critical_account_count || 0) > 0 ? "text-red-600" : "text-emerald-600")}>
                        {analysis.risk_recommendation.pattern_aggregate.recent_critical_account_count || 0}
                      </p>
                    </div>
                    <div data-testid="pattern-agg-recent-90">
                      <p className="text-xs text-muted-foreground">Códigos 4/5 últimos 12m</p>
                      <p className={cn("font-bold", (analysis.risk_recommendation.pattern_aggregate.last_12_late_90plus || 0) > 0 ? "text-red-600" : "text-emerald-600")}>
                        {analysis.risk_recommendation.pattern_aggregate.last_12_late_90plus || 0}
                      </p>
                    </div>
                    <div data-testid="pattern-agg-recent-30-60">
                      <p className="text-xs text-muted-foreground">Códigos 2/3 últimos 12m</p>
                      <p className={cn("font-bold", (analysis.risk_recommendation.pattern_aggregate.last_12_late_30 || 0) + (analysis.risk_recommendation.pattern_aggregate.last_12_late_60 || 0) > 0 ? "text-amber-600" : "text-emerald-600")}>
                        {(analysis.risk_recommendation.pattern_aggregate.last_12_late_30 || 0) + (analysis.risk_recommendation.pattern_aggregate.last_12_late_60 || 0)}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}


          {/* Risk Recommendation / Simulador de Riesgo */}
          {analysis?.risk_recommendation && (
            <Card className="border-2 border-slate-300" data-testid="risk-recommendation-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Simulador de Riesgo - Recomendación del Analista
                </CardTitle>
                <CardDescription>
                  Evaluación basada en historial de pagos de los últimos 3 años
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Risk level badges with lates observed */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { key: 'equipment_loan', label: 'Préstamo de Equipo', priority: true },
                    { key: 'business_loan', label: 'Préstamo Comercial', priority: false },
                    { key: 'credit_card', label: 'Tarjeta de Crédito', priority: false },
                    { key: 'rent', label: 'Renta', priority: false }
                  ].map(({ key, label, priority }) => {
                    const risk = analysis.risk_recommendation.risk_by_obligation?.[key];
                    if (!risk) return null;
                    const colors = {
                      'BAJO': 'bg-emerald-100 text-emerald-800 border-emerald-300',
                      'MEDIO': 'bg-amber-100 text-amber-800 border-amber-300',
                      'ALTO': 'bg-red-100 text-red-800 border-red-300',
                      'MUY ALTO': 'bg-red-200 text-red-900 border-red-400'
                    };
                    return (
                      <div key={key} className={`p-3 rounded-lg border ${colors[risk.level] || 'bg-gray-100'} ${priority ? 'ring-2 ring-purple-400' : ''}`} data-testid={`risk-${key}`}>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-medium opacity-70">{label}</p>
                          {priority && <Badge className="bg-purple-600 text-white text-[10px] px-1.5 py-0">PRIORITARIO</Badge>}
                        </div>
                        <p className="text-lg font-bold">{risk.level}</p>
                        {risk.lates_observed && (
                          <p className="text-[10px] opacity-70 mt-1">
                            Lates 30/60/90: <span className="font-mono">{risk.lates_observed['30']}/{risk.lates_observed['60']}/{risk.lates_observed['90']}</span>
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Overall risk */}
                <div className={cn(
                  "p-3 rounded-lg text-center",
                  analysis.risk_recommendation.overall_risk_level === 'BAJO' && "bg-emerald-50 border border-emerald-200",
                  analysis.risk_recommendation.overall_risk_level === 'MEDIO' && "bg-amber-50 border border-amber-200",
                  analysis.risk_recommendation.overall_risk_level === 'ALTO' && "bg-red-50 border border-red-200",
                  analysis.risk_recommendation.overall_risk_level === 'MUY ALTO' && "bg-red-100 border border-red-300"
                )} data-testid="overall-risk-level">
                  <p className="text-sm font-medium">Riesgo General de Default</p>
                  <p className="text-2xl font-bold">{analysis.risk_recommendation.overall_risk_level}</p>
                </div>
                
                {/* Written recommendation paragraphs */}
                <div className="space-y-3 mt-4 p-4 bg-slate-50 rounded-lg border" data-testid="risk-recommendation-text">
                  <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Análisis y Recomendación Escrita
                  </h4>
                  {analysis.risk_recommendation.recommendation_paragraphs?.map((para, idx) => (
                    <p key={idx} className="text-sm text-slate-700 leading-relaxed">
                      {para}
                    </p>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Executive Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Resumen Ejecutivo Automático
                </span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setEditingSummary(!editingSummary)}
                >
                  {editingSummary ? 'Cancelar' : 'Editar'}
                </Button>
              </CardTitle>
              <CardDescription>
                Este resumen se integra automáticamente en el Credit Summary Final
              </CardDescription>
            </CardHeader>
            <CardContent>
              {editingSummary ? (
                <div className="space-y-4">
                  <Textarea
                    value={summaryText}
                    onChange={(e) => setSummaryText(e.target.value)}
                    rows={6}
                    className="font-medium"
                  />
                  <Button onClick={handleSaveSummary}>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Cambios
                  </Button>
                </div>
              ) : (
                <div className={cn(
                  "p-4 rounded-lg whitespace-pre-wrap",
                  analysis?.status === 'clean_credit_review'
                    ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-300"
                    : "bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300"
                )}>
                  {analysis?.executive_summary || 'Sin resumen disponible'}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Analyst Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Notas del Analista
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={analystNotes}
                onChange={(e) => setAnalystNotes(e.target.value)}
                placeholder="Agregue sus observaciones y notas sobre este análisis..."
                rows={4}
              />
              <Button onClick={handleSaveNotes} disabled={savingNotes}>
                {savingNotes ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar Notas
              </Button>
            </CardContent>
          </Card>

          {/* Audit Trail */}
          <Card>
            <CardHeader>
              <CardTitle>Auditoría y Trazabilidad</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Archivo PDF</p>
                  <p className="font-medium">{analysis?.pdf_filename || 'No cargado'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Fecha de Análisis</p>
                  <p className="font-medium">
                    {analysis?.analyzed_at 
                      ? new Date(analysis.analyzed_at).toLocaleString() 
                      : 'Pendiente'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Creado Por</p>
                  <p className="font-medium">{analysis?.created_by}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total Tradelines</p>
                  <p className="font-medium">{analysis?.total_tradelines || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default CreditReportAnalysisApp;
